import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CountrySelect from "../components/CountrySelect.jsx";
import CustomSelect from "../components/CustomSelect.jsx";
import { useAuth } from "../hooks/useAuth.js";
import { clearPendingAttempts, getPendingAttempts } from "../utils/localScoreCache.js";

const US_STATES = [
  ["AL","Alabama"],["AK","Alaska"],["AZ","Arizona"],["AR","Arkansas"],["CA","California"],
  ["CO","Colorado"],["CT","Connecticut"],["DE","Delaware"],["FL","Florida"],["GA","Georgia"],
  ["HI","Hawaii"],["ID","Idaho"],["IL","Illinois"],["IN","Indiana"],["IA","Iowa"],
  ["KS","Kansas"],["KY","Kentucky"],["LA","Louisiana"],["ME","Maine"],["MD","Maryland"],
  ["MA","Massachusetts"],["MI","Michigan"],["MN","Minnesota"],["MS","Mississippi"],["MO","Missouri"],
  ["MT","Montana"],["NE","Nebraska"],["NV","Nevada"],["NH","New Hampshire"],["NJ","New Jersey"],
  ["NM","New Mexico"],["NY","New York"],["NC","North Carolina"],["ND","North Dakota"],["OH","Ohio"],
  ["OK","Oklahoma"],["OR","Oregon"],["PA","Pennsylvania"],["RI","Rhode Island"],["SC","South Carolina"],
  ["SD","South Dakota"],["TN","Tennessee"],["TX","Texas"],["UT","Utah"],["VT","Vermont"],
  ["VA","Virginia"],["WA","Washington"],["WV","West Virginia"],["WI","Wisconsin"],["WY","Wyoming"],
];

const COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "GB", name: "United Kingdom" },
  { code: "AU", name: "Australia" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "BR", name: "Brazil" },
  { code: "IN", name: "India" },
  { code: "MX", name: "Mexico" },
  { code: "NG", name: "Nigeria" },
  { code: "ZA", name: "South Africa" },
  { code: "NZ", name: "New Zealand" },
  { code: "IE", name: "Ireland" },
  { code: "PH", name: "Philippines" },
  { code: "PK", name: "Pakistan" },
];

const GoogleOnboardingPage = () => {
  const { googleComplete } = useAuth();
  const navigate = useNavigate();

  // Read onboarding state written by the login/register page
  const onboardingData = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("google_onboarding") || "null");
    } catch (_) {
      return null;
    }
  }, []);

  const pendingAttempts = useMemo(() => getPendingAttempts(), []);
  const pendingPoints = pendingAttempts.reduce((sum, a) => sum + (a.points_would_earn || 0), 0);

  const [fields, setFields] = useState({
    username: onboardingData?.suggested_name || "",
    country: "",
    state: "",
  });
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // If there's no onboarding session (e.g. direct navigation), go to register
  if (!onboardingData) {
    navigate("/register", { replace: true });
    return null;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFields((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "country" && value !== "US") next.state = "";
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await googleComplete({
        onboarding_token: onboardingData.onboarding_token,
        username: fields.username,
        country: fields.country,
        state: fields.state,
        ...(pendingAttempts.length > 0 && {
          pending_attempts: pendingAttempts.map(({ quiz_slug, attempt_token }) => ({
            quiz_slug,
            attempt_token,
          })),
        }),
      });
      sessionStorage.removeItem("google_onboarding");
      clearPendingAttempts();
      navigate("/", { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-sm pt-8">
      <h1 className="text-3xl font-semibold text-white">One last step</h1>
      <p className="mt-1 text-sm text-white/60">
        Signed in as <span className="text-white/80">{onboardingData.email}</span>. Choose a username to continue.
      </p>

      {pendingAttempts.length > 0 && (
        <div className="mt-6 rounded-2xl border border-brand-glow/30 bg-brand-glow/10 px-4 py-3">
          <p className="text-sm font-semibold text-white">
            {pendingAttempts.length === 1
              ? "You have 1 unsaved quiz score"
              : `You have ${pendingAttempts.length} unsaved quiz scores`}
          </p>
          <p className="mt-0.5 text-sm text-white/70">
            Complete sign-up to claim <span className="font-semibold text-brand-glow">{pendingPoints} pts</span>
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-8 space-y-4">
        <div>
          <label className="block text-sm font-medium text-white/80" htmlFor="username">
            Username <span className="text-red-400">*</span>
          </label>
          <input
            id="username"
            name="username"
            type="text"
            autoComplete="username"
            required
            value={fields.username}
            onChange={handleChange}
            className="mt-1 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-white/30 focus:border-brand-glow/60 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-white/80">Country</label>
          <div className="mt-1">
            <CountrySelect
              value={fields.country}
              onChange={(val) =>
                handleChange({ target: { name: "country", value: val } })
              }
              options={COUNTRIES.map((c) => ({ value: c.code, label: c.name }))}
              placeholder="— Select country —"
              variant="form"
            />
          </div>
        </div>

        {fields.country === "US" && (
          <div>
            <label className="block text-sm font-medium text-white/80">State</label>
            <div className="mt-1">
              <CustomSelect
                value={fields.state}
                onChange={(val) =>
                  handleChange({ target: { name: "state", value: val } })
                }
                options={[
                  { value: "", label: "— Select state —" },
                  ...US_STATES.map(([code, name]) => ({ value: code, label: name })),
                ]}
              />
            </div>
          </div>
        )}

        {error && <p className="text-sm text-red-300">{error}</p>}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-full bg-brand-glow px-6 py-3 text-sm font-semibold text-white disabled:opacity-50"
        >
          {submitting ? "Finishing up…" : "Finish sign-up"}
        </button>
      </form>
    </div>
  );
};

export default GoogleOnboardingPage;
